import { messagesApi } from "@/Api/Messages";
import { PopupVariant } from "@/UI/MessageAlerts/Popups/InterfacesAndEnums";
import { FileInfo } from "../FileInfo";
import { getFormatInfoGivenType, } from "../LoadSaveMolModels/Types/MolFormats";
import { OpenBabelQueue } from "./OpenBabelQueue";
import { getSetting } from "@/Plugins/Core/Settings/LoadSaveSettings";
import { isTest } from "@/Core/GlobalVars";
import { getFileType } from "../FileUtils2";
import { randomID } from "@/Core/Utils/MiscUtils";
import { detectBrowser, BrowserType } from "@/Core/HostOs";
export var WhichMolsGen3D;
(function (WhichMolsGen3D) {
    WhichMolsGen3D[WhichMolsGen3D["All"] = 0] = "All";
    WhichMolsGen3D[WhichMolsGen3D["None"] = 1] = "None";
    WhichMolsGen3D[WhichMolsGen3D["OnlyIfLacks3D"] = 2] = "OnlyIfLacks3D";
})(WhichMolsGen3D || (WhichMolsGen3D = {}));
export var Gen3DLevel;
(function (Gen3DLevel) {
    Gen3DLevel["None"] = "none";
    Gen3DLevel["Fastest"] = "fastest";
    Gen3DLevel["Fast"] = "fast";
    Gen3DLevel["Medium"] = "medium";
    Gen3DLevel["Better"] = "better";
    Gen3DLevel["Best"] = "best";
    // Default used to be medium for speed, but I was surprised to learn that
    // even ATP (basic molecule) had bad geometry unless best. SMILES I used for
    // testing:
    // O[C@@H]1[C@@H](CO[P@@](=O)(O[P@](=O)(OP(=O)(O)O)O)O)O[C@H]([C@@H]1O)n1cnc2c1ncnc2N
    // Default = "medium",
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    Gen3DLevel["Default"] = "best";
})(Gen3DLevel || (Gen3DLevel = {}));
/**
 * Detects whether a PDBQT file contains ligand-flexibility records (ROOT,
 * BRANCH, or TORSDOF). Ligand PDBQT — whether one molecule or many — always
 * has these markers. Rigid receptor PDBQT never does. Used to decide
 * whether OpenBabel's -m can be trusted to split the file: it works for
 * ligand PDBQT, but turns rigid receptors into nonsense by forcing
 * torsion generation on output.
 *
 * @param {string} contents  The raw PDBQT file contents.
 * @returns {boolean}  True if the file looks like ligand PDBQT.
 */
function pdbqtHasLigandFlexibility(contents) {
    // Anchor to line start so we don't match these tokens inside REMARK
    // text (e.g., "BRANCH" appearing in a comment).
    return /^(?:ROOT|BRANCH|TORSDOF)\b/m.test(contents);
}
/**
 * Gets the user argument select for specifying how to generate 3D coordinates.
 *
 * @param {string}  label              The label for the argument.
 * @param {string}  description        The description for the argument.
 * @param {boolean} includeNoneOption  Whether to include the "none" option.
 * @param {string}  defaultVal         The default value for the argument.
 * @returns {IUserArgSelect}  The user argument.
 */
export function getGen3DUserArg(label, description, includeNoneOption = false, defaultVal = Gen3DLevel.Default) {
    const options = [
        {
            description: "fastest: no forcefield optimization or conformer search",
            val: "fastest",
        },
        {
            description: "fast: quick forcefield optimization, no conformer search",
            val: "fast",
        },
        {
            description: "medium: quick forcefield optimization and fast conformer search",
            val: "medium",
        },
        {
            description: "better: medium forcefield optimization and fast conformer search",
            val: "better",
        },
        {
            description: "best: max forcefield optimization and thorough conformer search",
            val: "best",
        },
    ];
    // let defaultVal = "medium";
    if (includeNoneOption) {
        options.unshift({
            description: "recommended: max forcefield optimization and thorough conformer search",
            val: "best",
        });
        options.unshift({
            description: "none: do not generate 3D coordinates",
            val: "none",
        });
        defaultVal = Gen3DLevel.None;
    }
    return {
        label: label,
        description: description,
        id: "gen3D",
        val: defaultVal,
        options: options,
    };
}
/**
 * Runs OpenBabel.
 *
 * @param {string}       appId                   The app ID.
 * @param {string[][]}  argsLists                The arguments to pass to
 *                                               OpenBabel. Each set of
 *                                               arguments is a string[], so
 *                                               passing multiple argument sets
 *                                               (e.g., one for each input
 *                                               file), requires string[][].
 * @param {FileInfo[] | IFileInfo[]} inputFiles  The input files to pass to
 *                                               OpenBabel.
 * @param {boolean}      [surpressMsgs]          Whether to surpress messages.
 *                                               Default is false.
 * @returns {Promise<any>}  A promise that resolves to the output of the
 *     program. Void if there is an error?
 */
async function runOpenBabel(appId, argsLists, inputFiles, surpressMsgs = false) {
    // Quick validation to make sure argsLists is in right format.
    if (argsLists.length > 0 && !Array.isArray(argsLists[0])) {
        throw new Error("argsLists must be an array of arrays.");
    }
    // Associate an index with each inputFile so you can reorder them after
    // finishing.
    inputFiles.forEach((f, i) => {
        f.auxData = i;
    });
    inputFiles = inputFiles.map((f) => f.serialize ? f.serialize() : f);
    const isSafari = detectBrowser() === BrowserType.Safari;
    // Construct payloads by "zipping" the inputFiles and argsLists together.
    const payloads = [];
    for (let i = 0; i < inputFiles.length; i++) {
        payloads.push({
            args: argsLists[i],
            inputFile: inputFiles[i],
            surpressMsgs,
            isSafari,
        });
    }
    const maxProcs = (await getSetting("maxProcs"));
    return await new OpenBabelQueue(appId, payloads, maxProcs).done;
}
/**
 * Consider whether a warning is needed about lack of 3D coordinates. If needed,
 * show the warning.
 *
 * @param {IFormatInfo[]} formatInfos  The format infos.
 * @returns {any}  A timer that can be used to clear the warning if needed.
 */
function considerThreeDNeededWarning(formatInfos) {
    const warningNeeded = formatInfos.some((f) => f !== undefined && f.lacks3D === true);
    let msgTimer = undefined;
    if (warningNeeded && !isTest) {
        msgTimer = setTimeout(() => {
            // Warn user
            messagesApi.popupMessage("Converting Compounds to 3D", "<p>One or more input molecules does not include 3D coordinates. Currently calculating coordinates, which could take a while. Molecule(s) will appear in the Viewer when ready.</p><p>Tip: You can select the method for generating coordinates via <i>File → Open...</i> to speed up the calculation or improve the quality of the generated structures.</p>", PopupVariant.Warning, undefined, false, {});
        }, 2000);
    }
    return msgTimer;
}
/**
 * Gets the formats that OpenBabel can read and write. NOTE: this breaks
 * openbabel! It's not clear why. So just use it to see the formats (for
 * debugging), then uncomment.
 *
 * @returns {Promise<IFormatInfo[]>}  A promise that resolves to the formats.
 */
// export async function getObabelFormats(): Promise<IFormatInfo[]> {
//     const fakeFile = new FileInfo({
//         name: "fakeFile",
//         contents: "",
//     } as IFileInfo);
//     const obabelFormats = await runOpenBabel(
//         "getFormats",
//         [["-L", "formats"]],
//         // [["--version"]],
//         [fakeFile]
//     );
//     console.log(obabelFormats[0].stdOutOrErr);
//     return obabelFormats;
// }
/**
 * Separates a file into multiple files, one for each molecule.
 *
 * @param  {FileInfo[]}    srcFileInfos  The source file info.
 * @param  {IFormatInfo[]} formatInfos   The format infos.
 * @returns {Promise<FileInfo[]>}  A promise that resolves to the separated
 *    files.
 */
async function separateFiles(srcFileInfos, formatInfos) {
    // Use a per-call random prefix for intermediate files so they never
    // collide with user-supplied filenames. The worker's safety check
    // (which rejects input/output pairs with similar names) used to
    // throw "Input and output file names must be sufficiently different"
    // whenever a user file happened to be named `tmp.<ext>`. A random
    // prefix removes the entire collision class.
    const intermediatePrefix = `molmoda_intermediate_${randomID()}_`;
    const separateFileCmds = srcFileInfos.map((srcFileInfo, i) => {
        const inFmt = srcFileInfo.getFormatInfo();
        const cmds = [srcFileInfo.name];
        // When our internal extension differs from OpenBabel's known format
        // names (e.g., .pdbqtlig is our label for ligand PDBQT), declare
        // both input and output formats explicitly. The output extension
        // alone is not enough: OpenBabel infers output format from the -O
        // filename's extension, and an unknown extension yields "Missing
        // or unknown output file or format spec". We keep the .pdbqtlig
        // extension on disk so downstream stages re-resolve to the same
        // format entry, and use -o to tell OpenBabel the actual format.
        if (inFmt?.obabelFormatName) {
            cmds.push("-i", inFmt.obabelFormatName);
            cmds.push("-o", inFmt.obabelFormatName);
        }
        cmds.push("-m", "-O", `${intermediatePrefix}${i}.${inFmt?.primaryExt}`);
        return cmds;
    });
    const fileContentsFromInputs = await runOpenBabel("convertPrep", separateFileCmds, srcFileInfos);
    // Note that a given input molecule can yield multiple outputs if it
    // contained many molecules (e.g., multi-molecule SDF file)
    const individualMolFiles = fileContentsFromInputs.map((f) => f.outputFiles);
    // Convert them to a FileInfo
    let fileInfoIdx = -1;
    return individualMolFiles
        .map((fileContent, i) => {
        return fileContent.map((f) => {
            fileInfoIdx++;
            const ext = srcFileInfos[i].getFormatInfo()?.primaryExt;
            return {
                name: `${intermediatePrefix}${fileInfoIdx}.${ext}`,
                contents: f,
                auxData: formatInfos[i],
            };
        });
    })
        .flat();
}
/**
 * Converts molecules to another format using OpenBabel.
 *
 * @param  {FileInfo[]}    fileInfos             The information about the file
 *                                               to convert.
 * @param  {string}        targetFormat          The target extension.
 * @param  {IGen3DOptions} [gen3D]               Whether to assign 3D
 *                                               coordinates.
 * @param  {number | null} [pH]                  The pH to use for protonation.
 *                                               If null, removes hydrogen
 *                                               atoms.
 * @param  {boolean}       [desalt]        Whether to desalt the
 *                                               molecules.
 * @param  {boolean}       [surpressMsgs]  Whether to surpress messages.
 * @returns {Promise<string[]>}  A promise that resolves to the converted
 *    molecules.
 */
async function convertToNewFormat(fileInfos, targetFormat, gen3D, pH, desalt = false, surpressMsgs = false) {
    const cmdsList = fileInfos.map((fileInfo) => {
        const cmds = [fileInfo.name];
        // Declare the input format when our extension isn't one OpenBabel
        // recognizes natively. fileInfo may be a plain IFileInfo object
        // here (e.g., outputs of separateFiles), not a FileInfo class
        // instance, so we resolve the format via the standalone helpers
        // rather than calling fileInfo.getFormatInfo().
        const inTyp = getFileType(fileInfo);
        const inFmt = inTyp ? getFormatInfoGivenType(inTyp) : undefined;
        if (inFmt?.obabelFormatName) {
            cmds.push("-i", inFmt.obabelFormatName);
        }
        cmds.push("-m");
        if (desalt) {
            cmds.push(...["-r"]);
        }
        // If not specified, set to only if lacks 3d.
        let whichMols = gen3D?.whichMols === undefined
            ? WhichMolsGen3D.OnlyIfLacks3D
            : gen3D.whichMols;
        if (gen3D?.level === Gen3DLevel.None) {
            // A second way to say no 3D generation.
            whichMols = WhichMolsGen3D.None;
        }
        // If not specified, set to default
        const level = gen3D?.level === undefined ? Gen3DLevel.Default : gen3D.level;
        switch (whichMols) {
            case WhichMolsGen3D.All:
                cmds.push(...["--gen3D", level]);
                break;
            case WhichMolsGen3D.OnlyIfLacks3D:
                if (fileInfo.auxData && fileInfo.auxData.lacks3D === true) {
                    cmds.push(...["--gen3D", level]);
                }
                break;
            default:
                break;
        }
        // if (
        //     gen3D === true ||
        //     (fileInfo.auxData !== undefined &&
        //         fileInfo.auxData.lacks3D === true)
        // ) {
        //     cmds.push(...["--gen3D", "best"]);
        // }
        if (pH !== undefined && pH !== null) {
            cmds.push(...["-p", pH.toString()]);
        }
        else if (pH === null) {
            // Removes hydrogens. Good for 2D visualizations.
            cmds.push(...["-d"]);
        }
        // Are there additional arguments to pass to OpenBabel?
        const formatInfo = getFormatInfoGivenType(targetFormat);
        const extToUse = formatInfo?.obabelFormatName ?? targetFormat;
        cmds.push(...["-O", "tmpout." + extToUse]);
        if (formatInfo?.extraObabelArgs !== undefined) {
            cmds.push(...formatInfo.extraObabelArgs);
        }
        // console.log(cmds);
        return cmds;
    });
    // tmpPass = [cmdsList, fileInfos.map(f => JSON.parse(JSON.stringify(f)))];
    const convertedFileContents = await runOpenBabel("convert", cmdsList, fileInfos, surpressMsgs);
    // The output files are located in the .outputFiles properties.
    // Flatten them into one array.
    return convertedFileContents.map((c) => c.outputFiles).flat();
}
/**
 * Splits a multi-molecule CIF file into per-molecule CIF strings. OpenBabel's
 * CIF reader treats a .cif as a single crystallographic structure and stops
 * after the first `data_` block, so multi-molecule CIFs lose all molecules
 * after the first unless we split them ourselves before invoking OpenBabel.
 *
 * @param {string} contents  The full CIF file contents.
 * @returns {string[]}  One CIF block per molecule, each beginning with `data_`.
 *     Returns the original contents as a single-element array if no split is
 *     needed (i.e., zero or one `data_` blocks).
 */
function splitMultiMoleculeCif(contents) {
    // Locate every `data_` block start. We look for `data_` at column 0 to
    // avoid matching the substring inside comments or values.
    const blocks = [];
    const lines = contents.split("\n");
    let current = [];
    for (const line of lines) {
        if (line.startsWith("data_") && current.length > 0) {
            blocks.push(current.join("\n"));
            current = [];
        }
        current.push(line);
    }
    if (current.length > 0) {
        blocks.push(current.join("\n"));
    }
    // Filter out leading non-data preamble (anything before the first
    // `data_` line) if it accidentally became its own block.
    return blocks.filter((b) => b.split("\n").some((l) => l.startsWith("data_")));
}
/**
 * Converts a molecule to another format using OpenBabel.
 *
 * @param  {FileInfo[]}  srcFileInfos          The information about the file to
 *                                             convert.
 * @param  {string}      targetFormat          The target extension.
 * @param  {boolean}     [gen3D]               Whether to assign 3D coordinates.
 * @param  {number}      [pH]                  The pH to use for protonation. If
 *                                             null, removes hydrogens (-d).
 * @param  {boolean}     [desalt]        Whether to desalt the molecules.
 * @param  {boolean}     [surpressMsgs]  Whether to surpress messages.
 * @returns {Promise<string>}  A promise that resolves to the converted
 *     molecule.
 */
export async function convertFileInfosOpenBabel(srcFileInfos, // Can be multiple-model SDF file, for example.
targetFormat, gen3D, pH, desalt = false, surpressMsgs = false
// debug?: boolean
) {
    // Expand multi-molecule CIF inputs into one FileInfo per `data_` block.
    // OpenBabel's CIF reader only consumes the first block, so without this
    // pre-split, all molecules after the first are silently dropped. TODO:
    // Hackish? Might be nice if there was a generic preprocess system for any
    // filetype that defines it.
    const expandedSrcFileInfos = [];
    for (const f of srcFileInfos) {
        const ext = f.getFormatInfo()?.primaryExt;
        if (ext === "cif" || ext === "mcif") {
            const blocks = splitMultiMoleculeCif(f.contents);
            if (blocks.length > 1) {
                blocks.forEach((blockContents) => {
                    expandedSrcFileInfos.push(new FileInfo({
                        name: f.name,
                        contents: blockContents,
                    }));
                });
                continue;
            }
        }
        expandedSrcFileInfos.push(f);
    }
    srcFileInfos = expandedSrcFileInfos;
    // Get info about the file
    const formatInfos = srcFileInfos.map((f) => f.getFormatInfo());
    const msgTimer = considerThreeDNeededWarning(formatInfos);
    // Skip the separateFiles step for formats where the round-trip through
    // OpenBabel is lossy or otherwise problematic. CIF/MCIF strip polymer
    // entity info on intermediate writes. PDBQT receptors get rewritten as
    // flexible ligands (OpenBabel forces torsion generation on .pdbqt
    // output and produces a single ~29 MB file with bogus torsions instead
    // of cleanly splitting), so we skip separation when the PDBQT lacks
    // ligand flexibility records. Multi-ligand PDBQT still goes through
    // separateFiles, which works correctly for it.
    const skipSeparation = srcFileInfos.some((f, i) => {
        const ext = formatInfos[i]?.primaryExt;
        if (ext === "cif" || ext === "mcif")
            return true;
        if (ext === "pdbqt") {
            return !pdbqtHasLigandFlexibility(f.contents);
        }
        return false;
    });
    let fileInfos;
    if (skipSeparation) {
        fileInfos = srcFileInfos;
        // Initialize auxData if missing, as separateFiles usually does this
        fileInfos.forEach((f, i) => {
            if (!f.auxData)
                f.auxData = formatInfos[i];
        });
    }
    else {
        fileInfos = await separateFiles(srcFileInfos, formatInfos);
    }
    const outputFiles = await convertToNewFormat(fileInfos, targetFormat, gen3D, pH, desalt, surpressMsgs);
    // TODO: Report what's in the .stdOutAndErr property? Not sure needed.
    if (msgTimer !== undefined) {
        clearTimeout(msgTimer);
        // messagesApi.closePopupMessage();
    }
    return outputFiles;
    // .catch((err: any) => {
    //     throw err;
    // });
    // return runOpenBabel(cmds, [srcFileInfo])
    //     .then((convertedFileContents: any) => {
    //         messagesApi.closePopupMessage();
    //         return convertedFileContents.outputFiles;
    //     })
    //     .catch((err: any) => {
    //         throw err;
    //     });
}
